/**
 * zoom-logic.ts — pure scale/zoom math for the PDF editor.
 *
 * All functions here are side-effect-free: no DOM access, no `window`,
 * no `document`, no canvas context. Callers (editor-render.ts) pass in
 * the container and page dimensions they have already read from the DOM
 * so this module can be unit-tested without a browser environment.
 *
 * Extracted from `editor-render.ts` in the
 * ts-unit-testability-seams-260623 Epic.
 *
 * Exports:
 *   ZOOM_MIN, ZOOM_MAX, ZOOM_STEP     — zoom constants
 *   clampScale(s)                     — clamp to [ZOOM_MIN, ZOOM_MAX]
 *   roundToNearest(scale, step)       — round to nearest multiple of step
 *   fitToWidthScale(pageW, cW)        — fit-to-width scale (pure)
 *   fitToPageScale(pageW, pageH, cW, cH) — fit-to-page scale (pure)
 *   zoomStepUp(current, step?)        — one step up (clamped)
 *   zoomStepDown(current, step?)      — one step down (clamped)
 *   dropdownValueToScale(v)           — convert dropdown data-zoom string to scale
 */
// ─── Constants ────────────────────────────────────────────────────────────────
/** Step size for keyboard +/- and toolbar zoom in/out buttons. */
export const ZOOM_STEP = 0.25;
/** Minimum supported zoom level (10%). */
export const ZOOM_MIN = 0.10;
/** Maximum supported zoom level (500%). */
export const ZOOM_MAX = 5.0;
// ─── Primitive math ──────────────────────────────────────────────────────────
/**
 * Clamp `scale` to the supported [ZOOM_MIN, ZOOM_MAX] range.
 * Equivalent to `Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, scale))`.
 */
export function clampScale(scale) {
    return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, scale));
}
/**
 * Round `scale` to the nearest multiple of `step`.
 * Used to snap zoom in/out to the nearest 25-pp increment.
 */
export function roundToNearest(scale, step) {
    return Math.round(scale / step) * step;
}
// ─── Fit-to-container ────────────────────────────────────────────────────────
/**
 * Compute the scale so the page fills the container width with 24 px
 * padding on each side. Result is clamped to [ZOOM_MIN, ZOOM_MAX].
 *
 * @param pageNaturalWidth  Page width at scale=1 (PDF points).
 * @param containerWidth    CSS pixel width of the scroll container.
 */
export function fitToWidthScale(pageNaturalWidth, containerWidth) {
    // WHY: 48 = 24px padding each side — mirrors the CSS padding on
    // .pdf-scroll applied in the editor layout.
    const usableWidth = containerWidth - 48;
    return clampScale(usableWidth / pageNaturalWidth);
}
/**
 * Compute the scale so the whole page fits inside the container
 * (tightest-fit — uses the more constraining of width and height).
 * Result is clamped to [ZOOM_MIN, ZOOM_MAX].
 *
 * @param pageNaturalWidth   Page width at scale=1 (PDF points).
 * @param pageNaturalHeight  Page height at scale=1 (PDF points).
 * @param containerWidth     CSS pixel width of the scroll container.
 * @param containerHeight    CSS pixel height of the scroll container.
 */
export function fitToPageScale(pageNaturalWidth, pageNaturalHeight, containerWidth, containerHeight) {
    const usableWidth = containerWidth - 48;
    const usableHeight = containerHeight - 48;
    const scaleW = usableWidth / pageNaturalWidth;
    const scaleH = usableHeight / pageNaturalHeight;
    return clampScale(Math.min(scaleW, scaleH));
}
// ─── Step math ───────────────────────────────────────────────────────────────
/**
 * Compute the next zoom-in scale from `current`.
 * Snaps to the nearest step increment then adds one step, clamped to ZOOM_MAX.
 *
 * @param current  Current scale (finite, positive).
 * @param step     Step size (default ZOOM_STEP).
 */
export function zoomStepUp(current, step = ZOOM_STEP) {
    return Math.min(ZOOM_MAX, roundToNearest(current + step, step));
}
/**
 * Compute the next zoom-out scale from `current`.
 * Snaps to the nearest step increment then subtracts one step, clamped to ZOOM_MIN.
 *
 * @param current  Current scale (finite, positive).
 * @param step     Step size (default ZOOM_STEP).
 */
export function zoomStepDown(current, step = ZOOM_STEP) {
    return Math.max(ZOOM_MIN, roundToNearest(current - step, step));
}
// ─── Dropdown value mapping ───────────────────────────────────────────────────
/**
 * Convert a `data-zoom` attribute value from a zoom-dropdown button into a
 * numeric scale (0.10 – 5.0), or `null` if the value is a named mode.
 *
 * Named modes: "fit-width" → null (caller computes dynamically via DOM);
 *              "fit-page"  → null (caller computes dynamically via DOM).
 *
 * Numeric strings: "100" → 1.0, "50" → 0.50, "400" → 4.0.
 */
export function dropdownValueToScale(v) {
    if (v === "fit-width" || v === "fit-page")
        return null;
    const pct = parseInt(v, 10);
    if (!Number.isFinite(pct) || pct <= 0)
        return null;
    return pct / 100;
}
